import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Anchor,
  Button,
  Checkbox,
  Container,
  Group,
  Paper,
  PasswordInput,
  PinInput,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Colors } from "../../lib/Data";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  validateEmail,
} from "../../lib/Methods";
import { PerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/Endpoints";
import { NonPaginatedResponse } from "../../lib/Responses";

import "./styles.scss";
import AnimateOnLoad from "../../components/AnimateOnLoad";

const Login: React.FC = () => {
  const navigate = useNavigate();
  type LoginFormProps = {
    email: string;
    password: string;
  };
  const loginForm = useForm<LoginFormProps>({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value) => (validateEmail(value) ? null : "Enter a valid email"),
      password: (value) => (value.length > 5 ? null : "At least SIX characters"),
    },
  });
  const otpForm = useForm({
    initialValues: {
      code: "",
    },
    validate: {
      code: (value) => (value && value.length === 7 ? null : "Enter 7 digit OTP"),
    },
  });

  const [isLoading, setLoading] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<string>("");

  const [currentScreen, setCurrentScreen] = useState<"password" | "otp">("password");

  const InitiateLogin = async () => {
    const { hasErrors } = loginForm.validate();
    if (!hasErrors) {
      setLoading(true);
      const { email, password } = loginForm.values;
      const r = await PerformRequest<NonPaginatedResponse<{ token: string }>>({
        method: "POST",
        route: Endpoints.GetLoginAdmin(),
        data: {
          email,
          password,
        },
        callback: () => {
          setLoading(false);
        },
      });
      setLoading(false);
      if (r) {
        if (r.status === 200) {
          DefaultSuccessNotification("Login successful!");
          setCurrentScreen("otp");
          let count = 30;
          const intervalId = setInterval(() => {
            count -= 1;
            setCountdown(`${count}s`);

            // Stop the interval after 5 runs
            if (count === 0) {
              clearInterval(intervalId);
              setCountdown("");
              console.log("Interval stopped");
            }
          }, 1000); // Runs every 1 second
        }
        if (r.status === 404) {
          DefaultErrorNotification("Admin not found!");
        }
        if (r.status === 401) {
          DefaultErrorNotification("Incorrect password!");
        }
      } else {
        DefaultErrorNotification("An unexpected error occurred!");
      }
    }
  };
  const ResendOTP = async () => {
    const r = await PerformRequest({
      method: "GET",
      route: Endpoints.GetRequestOTP(loginForm.values.email),
    });
    if (r) {
      if (r.status === 200) {
        DefaultSuccessNotification("OTP sent to your email");
      } else {
        DefaultErrorNotification("Could not send OTP");
      }
    } else {
      DefaultErrorNotification("Could not send OTP");
    }
  };
  const FinishLogin = async () => {
    const { hasErrors } = otpForm.validate();
    if (!hasErrors) {
      setLoading(true);
      const { code } = otpForm.values;
      const { email, password } = loginForm.values;
      const r = await PerformRequest<NonPaginatedResponse<{ token: string }>>({
        method: "POST",
        route: Endpoints.GetVerifyOTPLogin(),
        data: {
          email,
          password,
          code,
        },
        callback: () => {
          setLoading(false);
        },
      });
      setLoading(false);
      if (r) {
        if (r.status === 200) {
          DefaultSuccessNotification("Login successful!");
          localStorage.setItem("t", r?.data?.data?.token);
          navigate("/dashboard");
        }
        if (r.status === 404) {
          DefaultErrorNotification("Invalid OTP!");
        }
        if (r.status === 410) {
          DefaultErrorNotification("Expired OTP!");
        }
      } else {
        DefaultErrorNotification("An unexpected error occurred!");
      }
    }
  };

  return (
    <AnimateOnLoad>
      <div className="login-container flex-col align-center justify-center">
        <Text ta="center" className="title">
          Welcome back!
        </Text>

        {currentScreen === "password" ? (
          <div className="form">
            <Paper withBorder shadow="md" p={30} mt={30} radius="md">
              <TextInput
                type="email"
                label="Email"
                placeholder="you@email.com"
                required
                spellCheck={false}
                {...loginForm.getInputProps("email")}
              />
              <PasswordInput
                label="Password"
                placeholder="Your password"
                required
                mt="md"
                {...loginForm.getInputProps("password")}
                spellCheck={false}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    InitiateLogin();
                  }
                }}
              />
              <Group justify="space-between" mt="lg">
                <Checkbox label="Remember me" />
                <Anchor component="button" size="sm">
                  <Link to="/reset" style={{ color: Colors.BluePrimary }}>
                    Forgot password?
                  </Link>
                </Anchor>
              </Group>
              <Button
                className="btn"
                color={Colors.GreenPrimary}
                onClick={InitiateLogin}
                fullWidth
                mt="xl"
                loading={isLoading}
              >
                Sign in
              </Button>
            </Paper>
          </div>
        ) : (
          <div className="form">
            <Paper withBorder shadow="md" p={30} mt={30} radius="md">
              <PinInput length={7} {...otpForm.getInputProps("code")} />
              <Group justify="space-between" mt="lg">
                <Anchor
                  onClick={() => {
                    setCurrentScreen("password");
                    setCountdown("");
                  }}
                  component="button"
                  size="sm"
                >
                  <Text c={Colors.BluePrimary}>Back</Text>
                </Anchor>
                <Button
                  disabled={countdown.length !== 0 || isLoading}
                  onClick={ResendOTP}
                  variant="subtle"
                  size="xs"
                  fw={400}
                >
                  Resend OTP {countdown && countdown.length > 0 ? `(${countdown})` : ""}
                </Button>
              </Group>
              <Button
                className="btn"
                color={Colors.GreenPrimary}
                onClick={FinishLogin}
                fullWidth
                mt="xl"
                loading={isLoading}
              >
                Sign In
              </Button>
            </Paper>
          </div>
        )}
      </div>
    </AnimateOnLoad>
  );
};

export default Login;
